import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Button, 
  Row, 
  Col, 
  Badge,
  Table,
  Spinner,
  Alert,
  Tabs,
  Tab,
  Form,
  Tooltip,
  OverlayTrigger
} from 'react-bootstrap';
import { 
  fetchStream, 
  updateStream,
  fetchStreams,
  regenerateStreamKey
} from '../../../store/actions/StreamActions';
import swal from 'sweetalert';

const StreamDetail = ({ 
  selectedStream, 
  loading, 
  error, 
  updating,
  regenerating,
  fetchStream, 
  updateStream,
  fetchStreams,
  regenerateStreamKey
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    fetchStream(id);
    
    // Refresh every 10 seconds
    const interval = setInterval(() => {
      fetchStream(id);
    }, 10000);

    return () => clearInterval(interval);
  }, [id, fetchStream]);

  useEffect(() => {
    if (selectedStream) {
      setEditForm({
        name: selectedStream.name || '',
        description: selectedStream.description || ''
      });
    }
  }, [selectedStream]);

  // Initialize HLS video player
  useEffect(() => {
    const loadHls = async () => {
      if (!selectedStream?.hls_url || !videoRef.current) return;
      
      const Hls = (await import('hls.js')).default;
      const video = videoRef.current;
      const hlsUrl = selectedStream.hls_url;
      
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
        });
        hls.loadSource(hlsUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => {});
        });
        // Without this, hls.js does not auto-recover from network/media errors -
        // a single transient blip (a late segment, a brief 404) leaves the player
        // stuck buffering forever even once the stream is healthy again.
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data.fatal) return;
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              hls.destroy();
              loadHls();
              break;
          }
        });
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = hlsUrl;
        video.addEventListener('loadedmetadata', () => {
          video.play().catch(() => {});
        });
      }
    };

    if (selectedStream?.hls_url) {
      loadHls();
    }
  }, [selectedStream?.hls_url]);

  const copyToClipboard = (text, label, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      swal("Copied!", `${label} copied to clipboard.`, "success", {
        timer: 1500,
        buttons: false
      });
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const renderCopyButton = (text, label, field) => (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{copiedField === field ? '✓ Copied!' : `Copy ${label}`}</Tooltip>}
    >
      <Button 
        variant={copiedField === field ? "success" : "outline-primary"} 
        size="sm"
        onClick={() => copyToClipboard(text, label, field)}
      >
        <i className={`fas fa-${copiedField === field ? 'check' : 'copy'}`}></i>
      </Button>
    </OverlayTrigger>
  );

  const getStatusBadge = (status, isLive) => {
    if (isLive) {
      return <Badge bg="success" className="px-3 py-2">● Live</Badge>;
    }
    
    switch (status) {
      case 'broadcasting':
        return <Badge bg="success" className="px-3 py-2">● Broadcasting</Badge>;
      case 'active':
        return <Badge bg="primary" className="px-3 py-2">Active</Badge>;
      case 'inactive':
        return <Badge bg="secondary" className="px-3 py-2">Inactive</Badge>;
      case 'disconnected':
        return <Badge bg="warning" className="px-3 py-2">Disconnected</Badge>;
      default:
        return <Badge bg="secondary" className="px-3 py-2">{status}</Badge>;
    }
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    updateStream(id, editForm, (err) => {
      if (!err) {
        swal("Updated!", "Stream details updated successfully.", "success");
        setIsEditing(false);
        fetchStream(id);
      } else {
        swal("Error!", "Failed to update stream.", "error");
      }
    });
  };

  const handleRegenerateKey = () => {
    swal({
      title: "Regenerate Stream Key?",
      text: "This will generate a new stream key. The old key will no longer work!",
      icon: "warning",
      buttons: ["Cancel", "Regenerate"],
      dangerMode: true,
    }).then((willRegenerate) => {
      if (willRegenerate) {
        regenerateStreamKey(id, (err) => {
          if (!err) {
            swal("Success!", "Stream key has been regenerated.", "success");
            fetchStream(id);
          } else {
            swal("Error!", "Failed to regenerate stream key.", "error");
          }
        });
      }
    });
  };

  if (loading && !selectedStream) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading stream details...</p>
      </div>
    );
  }

  if (!selectedStream && !loading) {
    return (
      <Alert variant="warning">
        <Alert.Heading>Stream Not Found</Alert.Heading>
        <p>The requested stream could not be found.</p>
        <Button variant="primary" onClick={() => navigate('/streams')}>
          Back to Streams
        </Button>
      </Alert>
    );
  }

  return (
    <>
      <div className="form-head d-flex mb-3 mb-md-4 align-items-start">
        <div className="me-auto d-none d-lg-block">
          <Button 
            variant="link" 
            className="p-0 mb-2"
            onClick={() => navigate('/streams')}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Streams
          </Button>
          <h3 className="text-black font-w600">
            {selectedStream?.name}
            <span className="ms-3">
              {getStatusBadge(selectedStream?.status, selectedStream?.is_live)}
            </span>
          </h3>
          <p className="mb-0">{selectedStream?.description || 'No description'}</p>
        </div>
        <div>
          <Button 
            variant="outline-primary" 
            className="me-2"
            onClick={() => setIsEditing(!isEditing)}
          >
            <i className={`fas fa-${isEditing ? 'times' : 'edit'} me-2`}></i>
            {isEditing ? 'Cancel' : 'Edit'}
          </Button>
          <Button 
            variant="outline-danger"
            onClick={() => navigate('/streams')}
          >
            <i className="fas fa-list me-2"></i>
            All Streams
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible className="mb-4">
          {error}
        </Alert>
      )}

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        <Tab eventKey="overview" title="Overview">
          <Row>
            <Col lg={8}>
              {isEditing ? (
                <Card className="mb-4">
                  <Card.Body>
                    <h5 className="mb-4">Edit Stream</h5>
                    <Form onSubmit={handleEditSubmit}>
                      <Form.Group className="mb-3">
                        <Form.Label>Stream Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                          required
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={editForm.description}
                          onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                        />
                      </Form.Group>
                      <div className="d-flex gap-2">
                        <Button 
                          variant="secondary" 
                          onClick={() => setIsEditing(false)}
                        >
                          Cancel
                        </Button>
                        <Button 
                          variant="primary" 
                          type="submit"
                          disabled={updating}
                        >
                          {updating ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Saving...
                            </>
                          ) : (
                            'Save Changes'
                          )}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              ) : null}

              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-link me-2"></i>
                    Stream URLs
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Table borderless>
                    <tbody>
                      <tr>
                        <td className="fw-bold" style={{width: '150px'}}>RTMP URL:</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.rtmp_url}
                          </code>
                          <small className="text-muted">
                            Use this URL in OBS Studio or other broadcasting software
                          </small>
                        </td>
                        <td style={{width: '100px'}}>
                          {renderCopyButton(selectedStream?.rtmp_url, 'RTMP URL', 'rtmp')}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Stream Key:</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.stream_key}
                          </code>
                          <small className="text-muted">
                            Unique identifier for this stream
                          </small>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            {renderCopyButton(selectedStream?.stream_key, 'Stream Key', 'key')}
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>Regenerate stream key</Tooltip>}
                            >
                              <Button 
                                variant="outline-warning" 
                                size="sm"
                                onClick={handleRegenerateKey}
                                disabled={regenerating}
                              >
                                <i className={`fas fa-sync-alt ${regenerating ? 'fa-spin' : ''}`}></i>
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">HLS URL (Primary):</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.hls_url}
                          </code>
                          <small className="text-muted">
                            Primary HLS stream URL for playback
                          </small>
                        </td>
                        <td>
                          {renderCopyButton(selectedStream?.hls_url, 'HLS URL', 'hls')}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">HLS URL (Backup):</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.hls_backup_url || selectedStream?.hls_url?.replace('.m3u8', '_backup.m3u8')}
                          </code>
                          <small className="text-muted">
                            Backup HLS stream URL (lower quality)
                          </small>
                        </td>
                        <td>
                          {renderCopyButton(selectedStream?.hls_backup_url || selectedStream?.hls_url?.replace('.m3u8', '_backup.m3u8'), 'HLS Backup URL', 'hls_backup')}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">DASH URL:</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.dash_url || selectedStream?.hls_url?.replace('/hls/', '/dash/').replace('.m3u8', '.mpd')}
                          </code>
                          <small className="text-muted">
                            MPEG-DASH format for modern browsers
                          </small>
                        </td>
                        <td>
                          {renderCopyButton(selectedStream?.dash_url || selectedStream?.hls_url?.replace('/hls/', '/dash/').replace('.m3u8', '.mpd'), 'DASH URL', 'dash')}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Audio Only (MP3):</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.audio_url || selectedStream?.hls_url?.replace('/hls/', '/audio/').replace('.m3u8', '.mp3')}
                          </code>
                          <small className="text-muted">
                            Audio-only stream for low bandwidth
                          </small>
                        </td>
                        <td>
                          {renderCopyButton(selectedStream?.audio_url || selectedStream?.hls_url?.replace('/hls/', '/audio/').replace('.m3u8', '.mp3'), 'Audio URL', 'audio')}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              {/* Video Player */}
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-play-circle me-2"></i>
                    Live Preview
                  </h5>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="position-relative bg-dark">
                    <video
                      ref={videoRef}
                      className="w-100"
                      style={{ height: '300px', backgroundColor: '#000' }}
                      controls
                      muted
                      playsInline
                      poster="/images/video-placeholder.jpg"
                    />
                    {selectedStream?.is_live && (
                      <Badge 
                        bg="danger" 
                        className="position-absolute top-0 start-0 m-3"
                        style={{ fontSize: '12px' }}
                      >
                        ● LIVE
                      </Badge>
                    )}
                  </div>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-chart-line me-2"></i>
                    Live Status
                  </h5>
                </Card.Header>
                <Card.Body>
                  {selectedStream?.is_live ? (
                    <div className="text-center py-4">
                      <div className="mb-3">
                        <span className="display-1 text-success">●</span>
                      </div>
                      <h4 className="text-success">Stream is Live!</h4>
                      <p className="text-muted">
                        {selectedStream?.live_status?.clients || 0} viewers currently watching
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="mb-3">
                        <span className="display-1 text-secondary">○</span>
                      </div>
                      <h4 className="text-muted">Stream is Offline</h4>
                      <p className="text-muted">
                        Start streaming to see live preview
                      </p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Stream Stats</h5>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <small className="text-muted d-block">Viewers</small>
                    <h4 className="mb-0">
                      {selectedStream?.viewer_count || 0}
                      {selectedStream?.is_live && (
                        <span className="text-success ms-2" style={{fontSize: '14px'}}>
                          ● Live
                        </span>
                      )}
                    </h4>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block">Bandwidth Usage</small>
                    <h4 className="mb-0">
                      {selectedStream?.bandwidth_usage 
                        ? `${(selectedStream.bandwidth_usage / 1024 / 1024).toFixed(2)} MB`
                        : '0 MB'
                      }
                    </h4>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block">Created</small>
                    <span>
                      {selectedStream?.created_at 
                        ? new Date(selectedStream.created_at).toLocaleString()
                        : '-'
                      }
                    </span>
                  </div>
                  <div className="mb-0">
                    <small className="text-muted d-block">Last Active</small>
                    <span>
                      {selectedStream?.last_active_at 
                        ? new Date(selectedStream.last_active_at).toLocaleString()
                        : 'Never'
                      }
                    </span>
                  </div>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <h5 className="mb-0">Quick Actions</h5>
                </Card.Header>
                <Card.Body>
                  <Button 
                    variant="outline-primary" 
                    className="w-100 mb-2"
                    onClick={() => copyToClipboard(selectedStream?.rtmp_url, 'RTMP URL', 'rtmp')}
                  >
                    <i className="fas fa-copy me-2"></i>
                    Copy RTMP URL
                  </Button>
                  <Button 
                    variant="outline-success" 
                    className="w-100 mb-2"
                    onClick={() => copyToClipboard(selectedStream?.hls_url, 'HLS URL', 'hls')}
                  >
                    <i className="fas fa-play me-2"></i>
                    Copy HLS URL
                  </Button>
                  <Button 
                    variant="outline-info" 
                    className="w-100"
                    onClick={() => fetchStream(id)}
                  >
                    <i className="fas fa-sync-alt me-2"></i>
                    Refresh Status
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="logs" title="Activity Logs">
          <Card>
            <Card.Body>
              <h5 className="mb-4">Recent Activity</h5>
              {selectedStream?.recent_logs && selectedStream.recent_logs.length > 0 ? (
                <div className="timeline">
                  {selectedStream.recent_logs.map((log, index) => (
                    <div key={index} className="timeline-item mb-3 pb-3 border-bottom">
                      <div className="d-flex">
                        <div className="timeline-icon me-3">
                          <i className={`fas fa-${
                            log.event_type.includes('publish') ? 'upload' :
                            log.event_type.includes('playback') ? 'play' :
                            log.event_type.includes('status') ? 'chart-line' :
                            'info-circle'
                          } text-primary`}></i>
                        </div>
                        <div>
                          <h6 className="mb-1">{log.event_type}</h6>
                          <small className="text-muted">
                            {new Date(log.created_at).toLocaleString()}
                          </small>
                          {log.event_data && (
                            <pre className="mt-2 mb-0 bg-light p-2 rounded">
                              <small>{JSON.stringify(JSON.parse(log.event_data), null, 2)}</small>
                            </pre>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-chart-line fa-3x mb-3"></i>
                  <p>No activity logs available</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </>
  );
};

const mapStateToProps = (state) => ({
  selectedStream: state.streams.selectedStream,
  loading: state.streams.loading,
  error: state.streams.error,
  updating: state.streams.updating,
  regenerating: state.streams.regenerating
});

const mapDispatchToProps = {
  fetchStream,
  updateStream,
  fetchStreams,
  regenerateStreamKey
};

export default connect(mapStateToProps, mapDispatchToProps)(StreamDetail);
